import { useState, useEffect } from 'react';
import ContentCard from '../../../../../../components/layout/ContentCard/ContentCard';
import Button from '../../../../../../components/common/Button/Button';
import Badge from '../../../../../../components/common/Badge/Badge';
import Input from '../../../../../../components/common/Input/Input';
import { translate } from '../../../../../../utils/Common';
import { FiBriefcase, FiUsers } from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import {
  formatCompanyName,
  formatPrimaryContact,
  formatEmail,
} from '../../utils/companyFormatters';
import styles from '../../CompanyDetail.module.css';

const OverviewTab = ({ company, onUpdateCompany, canEdit = true }) => {
  const [isEditingCompanyInfo, setIsEditingCompanyInfo] = useState(false);
  const [isEditingContactInfo, setIsEditingContactInfo] = useState(false);
  
  // Form states for Company Info
  const [companyName, setCompanyName] = useState('');
  const [lockerConsolidation, setLockerConsolidation] = useState(false);
  const [isActive, setIsActive] = useState(true);
  
  // Form states for Contact Info
  const [contactFirstName, setContactFirstName] = useState('');
  const [contactLastName, setContactLastName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  
  // Loading and error states
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Initialize form values when company data is available
  useEffect(() => {
    if (company) {
      setCompanyName(company?.companyName || company?.NombreEmpresa || '');
      setLockerConsolidation(company?.lockerConsolidation !== undefined ? company.lockerConsolidation : false);
      setIsActive(company?.isActive !== undefined ? company.isActive : (company?.Activo !== undefined ? company.Activo : true));
      setContactFirstName(company?.contactPrimaryFirstName || company?.PrimerNombre || '');
      setContactLastName(company?.contactPrimaryLastName || company?.PrimerApellido || '');
      setContactEmail(company?.contactPrimaryEmail || company?.Email || '');
    }
  }, [company]);

  if (!company) return null;

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleStartEditCompanyInfo = () => {
    setIsEditingCompanyInfo(true);
    setSaveError('');
    setSaveSuccess(false);
  };

  const handleCancelEditCompanyInfo = () => {
    setIsEditingCompanyInfo(false);
    setCompanyName(company?.companyName || company?.NombreEmpresa || '');
    setLockerConsolidation(company?.lockerConsolidation !== undefined ? company.lockerConsolidation : false);
    setIsActive(company?.isActive !== undefined ? company.isActive : (company?.Activo !== undefined ? company.Activo : true));
    setSaveError('');
    setSaveSuccess(false);
  };

  const handleSaveCompanyInfo = async () => {
    setSaveError('');
    setSaveSuccess(false);
    
    if (!companyName.trim()) {
      setSaveError(translate('dashboard.companies.detail.overview.validation.companyNameRequired'));
      return;
    }

    setIsSaving(true);
    try {
      // Send all required fields: edited fields with new values, others with current values
      const result = await onUpdateCompany({
        companyName: companyName.trim(),
        lockerConsolidation,
        isActive,
        // Include current contact info values
        contactPrimaryEmail: company?.contactPrimaryEmail || company?.Email || '',
        contactPrimaryFirstName: company?.contactPrimaryFirstName || company?.PrimerNombre || '',
        contactPrimaryLastName: company?.contactPrimaryLastName || company?.PrimerApellido || '',
      });
      
      if (result.success) {
        setSaveSuccess(true);
        setIsEditingCompanyInfo(false);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setSaveError(result.error || translate('dashboard.companies.detail.overview.errors.saveCompanyInfoError'));
      }
    } catch (error) {
      setSaveError(error.message || translate('dashboard.companies.detail.overview.errors.saveCompanyInfoError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartEditContactInfo = () => {
    setIsEditingContactInfo(true);
    setSaveError('');
    setSaveSuccess(false);
  };

  const handleCancelEditContactInfo = () => {
    setIsEditingContactInfo(false);
    setContactFirstName(company?.contactPrimaryFirstName || company?.PrimerNombre || '');
    setContactLastName(company?.contactPrimaryLastName || company?.PrimerApellido || '');
    setContactEmail(company?.contactPrimaryEmail || company?.Email || '');
    setSaveError('');
    setSaveSuccess(false);
  };

  const handleSaveContactInfo = async () => {
    setSaveError('');
    setSaveSuccess(false);
    
    if (!contactEmail.trim()) {
      setSaveError(translate('dashboard.companies.detail.overview.validation.emailRequired'));
      return;
    }

    if (!validateEmail(contactEmail.trim())) {
      setSaveError(translate('dashboard.companies.detail.overview.validation.emailInvalid'));
      return;
    }

    setIsSaving(true);
    try {
      // Send all required fields: edited fields with new values, others with current values
      const result = await onUpdateCompany({
        // Include current company info values
        companyName: company?.companyName || company?.NombreEmpresa || '',
        lockerConsolidation: company?.lockerConsolidation !== undefined ? company.lockerConsolidation : false,
        // New contact info values
        contactPrimaryFirstName: contactFirstName.trim(),
        contactPrimaryLastName: contactLastName.trim(),
        contactPrimaryEmail: contactEmail.trim(),
        // Include isActive if available
        isActive: company?.isActive !== undefined ? company.isActive : (company?.Activo !== undefined ? company.Activo : true)
      });
      
      if (result.success) {
        setSaveSuccess(true);
        setIsEditingContactInfo(false);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setSaveError(result.error || translate('dashboard.companies.detail.overview.errors.saveContactInfoError'));
      }
    } catch (error) {
      setSaveError(error.message || translate('dashboard.companies.detail.overview.errors.saveContactInfoError'));
    } finally {
      setIsSaving(false);
    }
  };


  return (
    <div className={styles.overviewContainer}>
      {(saveError || saveSuccess) && (
        <div style={{ 
          marginBottom: '1rem', 
          padding: '0.75rem 1rem', 
          borderRadius: '8px',
          backgroundColor: saveSuccess ? '#d4edda' : '#f8d7da',
          color: saveSuccess ? '#155724' : '#721c24',
          border: `1px solid ${saveSuccess ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {saveSuccess ? translate('dashboard.companies.detail.overview.messages.saveSuccess') : `✗ ${saveError}`}
        </div>
      )}
      
      <div className="flex flex-wrap">
        {/* Company Info Card */}
        <div className="lg:w-6/12 md:w-full mb-4">
          <ContentCard 
            title={
              <div className={styles.cardTitle}>
                <FiBriefcase size={20} />
                <span>{translate('dashboard.companies.detail.overview.sections.companyInfo.title')}</span>
              </div>
            }
            padding="md"
            className={styles.infoCard}
            headerActions={
              canEdit && (
              !isEditingCompanyInfo ? (
                <Button 
                  variant="ghostOutline" 
                  onClick={handleStartEditCompanyInfo}
                  leftIcon={<FaIcons.FaEdit />}
                  className={styles.sectionEditButton}
                >
                  {translate('global.edit')}
                </Button>
              ) : (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button 
                    variant="primary" 
                    onClick={handleSaveCompanyInfo}
                    leftIcon={<FaIcons.FaSave />}
                    className={styles.sectionEditButton}
                    loading={isSaving}
                    disabled={isSaving}
                  >
                    {translate('global.save')}
                  </Button>
                  <Button 
                    variant="ghostOutline" 
                    onClick={handleCancelEditCompanyInfo}
                    className={styles.sectionEditButton}
                    disabled={isSaving}
                  >
                    {translate('global.cancel')}
                  </Button>
                </div>
              )
            )
            }
          >
            <div className={styles.infoList}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{translate('dashboard.companies.detail.overview.fields.companyName')}</span>
                {isEditingCompanyInfo ? (
                  <Input
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder={translate('dashboard.companies.detail.overview.fields.companyName.placeholder')}
                    fullWidth
                    required
                  />
                ) : (
                  <span className={styles.infoValue}>{formatCompanyName(company)}</span>
                )}
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{translate('dashboard.companies.detail.overview.fields.boxitCompanyCode')}</span>
                <span className={styles.infoValue}>{company?.boxitCompanyCode || 'N/A'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{translate('dashboard.companies.detail.overview.fields.companyCode')}</span>
                <span className={styles.infoValue}>{company?.companyCode || company?.CodUser || 'N/A'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{translate('dashboard.companies.detail.overview.fields.dropoffRegex')}</span>
                <span className={styles.infoValue}>{company?.dropoffregex || 'N/A'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{translate('dashboard.companies.detail.overview.fields.pickupRegex')}</span>
                <span className={styles.infoValue}>{company?.pickupregex || 'N/A'}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{translate('dashboard.companies.detail.overview.fields.lockerConsolidation')}</span>
                {isEditingCompanyInfo ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={lockerConsolidation}
                      onChange={(e) => setLockerConsolidation(e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                      {lockerConsolidation ? translate('global.enabled') : translate('global.disabled')}
                    </span>
                  </div>
                ) : (
                  <div className={styles.infoValue}>
                    {company?.lockerConsolidation !== undefined ? (
                      <Badge 
                        variant={company.lockerConsolidation ? 'success' : 'error'}
                        icon={company.lockerConsolidation ? <FaIcons.FaCheck /> : <FaIcons.FaTimes />}
                        showIcon={true}
                      />
                    ) : (
                      <Badge variant="error" icon={<FaIcons.FaTimes />} showIcon={true} />
                    )}
                  </div>
                )}
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{translate('dashboard.companies.detail.overview.fields.status')}</span>
                {isEditingCompanyInfo ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                      {isActive ? translate('dashboard.companies.status.active') : translate('dashboard.companies.status.inactive')}
                    </span>
                  </div>
                ) : (
                  <div className={styles.infoValue}>
                    {(() => {
                      const companyIsActive = company?.isActive !== undefined ? company.isActive : (company?.Activo !== undefined ? company.Activo : true);
                      return (
                        <Badge 
                          variant={companyIsActive ? 'success' : 'warning'}
                          icon={companyIsActive ? <FaIcons.FaCheck /> : <FaIcons.FaTimes />}
                          showIcon={true}
                        >
                          {companyIsActive ? translate('dashboard.companies.status.active') : translate('dashboard.companies.status.inactive')}
                        </Badge>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </ContentCard>
        </div>

        {/* Contact Info Card */}
        <div className="lg:w-6/12 md:w-full mb-4">
          <ContentCard 
            title={
              <div className={styles.cardTitle}>
                <FiUsers size={20} />
                <span>{translate('dashboard.companies.detail.overview.sections.contactInfo.title')}</span>
              </div>
            }
            padding="md"
            className={styles.infoCard}
            headerActions={
              canEdit && (
              !isEditingContactInfo ? (
                <Button 
                  variant="ghostOutline" 
                  onClick={handleStartEditContactInfo}
                  leftIcon={<FaIcons.FaEdit />}
                  className={styles.sectionEditButton}
                >
                  {translate('global.edit')}
                </Button>
              ) : (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button 
                    variant="primary" 
                    onClick={handleSaveContactInfo}
                    leftIcon={<FaIcons.FaSave />}
                    className={styles.sectionEditButton}
                    loading={isSaving}
                    disabled={isSaving}
                  >
                    {translate('global.save')}
                  </Button>
                  <Button 
                    variant="ghostOutline" 
                    onClick={handleCancelEditContactInfo}
                    className={styles.sectionEditButton}
                    disabled={isSaving}
                  >
                    {translate('global.cancel')}
                  </Button>
                </div>
              )
            )
            }
          >
            <div className={styles.infoList}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{translate('dashboard.companies.detail.overview.fields.firstName')}</span>
                {isEditingContactInfo ? (
                  <Input
                    value={contactFirstName}
                    onChange={(e) => setContactFirstName(e.target.value)}
                    placeholder={translate('dashboard.companies.detail.overview.fields.firstName.placeholder')}
                    fullWidth
                  />
                ) : (
                  <span className={styles.infoValue}>{company?.contactPrimaryFirstName || company?.PrimerNombre || 'N/A'}</span>
                )}
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{translate('dashboard.companies.detail.overview.fields.lastName')}</span>
                {isEditingContactInfo ? (
                  <Input
                    value={contactLastName}
                    onChange={(e) => setContactLastName(e.target.value)}
                    placeholder={translate('dashboard.companies.detail.overview.fields.lastName.placeholder')}
                    fullWidth
                  />
                ) : (
                  <span className={styles.infoValue}>{company?.contactPrimaryLastName || company?.PrimerApellido || 'N/A'}</span>
                )}
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{translate('dashboard.companies.detail.overview.fields.email')}</span>
                {isEditingContactInfo ? (
                  <Input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder={translate('dashboard.companies.detail.overview.fields.email.placeholder')}
                    fullWidth
                    required
                    error={saveError && saveError.includes('email') ? saveError : ''}
                  />
                ) : (
                  <span className={styles.infoValue}>{formatEmail(company)}</span>
                )}
              </div>
            </div>
          </ContentCard>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;

